import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { 
  Settings as SettingsIcon, 
  Bot, 
  Key, 
  Save, 
  CheckCircle, 
  AlertCircle, 
  Sun, 
  Moon,
  Database,
  Shield,
  User,
  Bell,
  Palette,
  Zap,
  Eye,
  EyeOff,
  Droplets,
  Snowflake,
  Sparkles,
  Leaf,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  Info,
  Lightbulb,
  Target,
  Play,
  Pause,
  Volume2,
  VolumeX,
  RefreshCw,
  Download,
  Trash2,
  Plus,
  MessageSquare,
  TrendingUp,
  Users,
  BarChart3,
  Activity,
  Award,
  Clock,
  Star
} from 'lucide-react';
import { useTheme, ThemeType, ColorMode } from '../contexts/ThemeContext';
import '../index.css';

interface AIConfig {
  provider: string;
  api_key: string;
  model: string;
}

interface Settings {
  ai_config: AIConfig;
}

const Settings: React.FC = () => {
  const { currentTheme, setTheme, isParticlesActive, toggleParticles, getThemeColors, colorMode, setColorMode } = useTheme();
  const colors = getThemeColors();
  
  const [settings, setSettings] = useState<Settings>({
    ai_config: {
      provider: 'openai',
      api_key: '',
      model: 'gpt-4o-mini'
    }
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['ai', 'appearance']));

  const themes = [
    {
      id: 'default' as ThemeType,
      name: 'Ocean',
      icon: <Droplets className="w-5 h-5" />,
      description: 'Classic blue and purple',
      gradient: 'from-blue-500 to-purple-600',
      preview: 'bg-gradient-to-r from-blue-500 to-purple-600'
    },
    {
      id: 'green' as ThemeType,
      name: 'Forest',
      icon: <Leaf className="w-5 h-5" />,
      description: 'Fresh green and emerald',
      gradient: 'from-emerald-500 to-green-600',
      preview: 'bg-gradient-to-r from-emerald-500 to-green-600'
    },
    {
      id: 'bubbles' as ThemeType,
      name: 'Bubbles',
      icon: <Sparkles className="w-5 h-5" />,
      description: 'Playful cyan bubbles',
      gradient: 'from-cyan-500 to-blue-600',
      preview: 'bg-gradient-to-r from-cyan-500 to-blue-600'
    },
    {
      id: 'raindrops' as ThemeType,
      name: 'Raindrops',
      icon: <Droplets className="w-5 h-5" />,
      description: 'Cool blue raindrops',
      gradient: 'from-blue-500 to-indigo-600',
      preview: 'bg-gradient-to-r from-blue-500 to-indigo-600'
    },
    {
      id: 'snow' as ThemeType,
      name: 'Snow',
      icon: <Snowflake className="w-5 h-5" />,
      description: 'Elegant snowflakes',
      gradient: 'from-slate-500 to-gray-600',
      preview: 'bg-gradient-to-r from-slate-500 to-gray-600'
    },
    {
      id: 'floating' as ThemeType,
      name: 'Floating',
      icon: <Zap className="w-5 h-5" />,
      description: 'Magical floating particles',
      gradient: 'from-purple-500 to-pink-600',
      preview: 'bg-gradient-to-r from-purple-500 to-pink-600'
    }
  ];

  useEffect(() => {
    fetchSettings();
    // Sync displayed switch with actual mode for legacy UI
    const isDark = document.documentElement.classList.contains('dark');
    setIsDarkMode(isDark);
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/api/settings');
      setSettings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.post('/api/settings', settings);
      
      if (settings.ai_config.api_key) {
        setMessage({ type: 'success', text: '✅ API key saved successfully! You can now use the AI assistant.' });
      } else {
        setMessage({ type: 'success', text: 'Settings saved successfully!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const testAIConnection = async () => {
    if (!settings.ai_config.api_key) {
      setMessage({ type: 'error', text: 'Please enter your API key first.' });
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/api/ai/test', {
        api_key: settings.ai_config.api_key,
        provider: settings.ai_config.provider,
        model: settings.ai_config.model
      });
      
      if (response.data.success) {
        setMessage({ type: 'success', text: 'AI connection successful! Your API key is working.' });
      } else {
        setMessage({ type: 'error', text: response.data.error || 'AI connection failed.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'AI connection failed. Please check your API key.' });
    } finally {
      setSaving(false);
    }
  };

  const toggleDarkMode = () => {
    const newDark = !document.documentElement.classList.contains('dark');
    setIsDarkMode(newDark);
    setColorMode(newDark ? 'dark' : 'light');
  };

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const exportData = async () => {
    try {
      const [tasksRes, projectsRes] = await Promise.all([
        api.get('/api/tasks'),
        api.get('/api/projects')
      ]);
      
      const data = {
        tasks: tasksRes.data,
        projects: projectsRes.data,
        export_date: new Date().toISOString()
      };
      
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `tess_export_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      
      setMessage({ type: 'success', text: 'Data exported successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to export data.' });
    }
  };

  const clearData = async () => {
    if (!window.confirm('Are you sure you want to clear all data? This action cannot be undone.')) {
      return;
    }
    
    try {
      await api.delete('/api/clear-data');
      setMessage({ type: 'success', text: 'All data cleared successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to clear data.' });
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
        <div className="container mx-auto px-6 py-8">
          <div className="h-8 w-44 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-xl p-6 border border-slate-200/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-800/60">
                <div className="h-6 w-52 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse mb-4" />
                <div className="space-y-2">
                  <div className="h-4 w-full bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                  <div className="h-4 w-5/6 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                  <div className="h-4 w-2/3 bg-slate-200 dark:bg-slate-700 rounded-md animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.background}`}>
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className={`text-3xl font-bold bg-gradient-to-r ${colors.primary} bg-clip-text text-transparent mb-2`}>
                Settings
              </h1>
              <p className={colors.textSecondary}>
                Configure your Tess Project Manager preferences and appearance
              </p>
            </div>
            <button 
              onClick={saveSettings}
              disabled={saving}
              className={`px-6 py-3 bg-gradient-to-r ${colors.gradient} hover:${colors.gradientHover} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl transition-all duration-200 flex items-center space-x-2 font-medium shadow-lg hover:shadow-xl`}
            >
              <Save className="w-5 h-5" />
              <span>{saving ? 'Saving...' : 'Save Settings'}</span>
            </button>
          </div>

          {/* Message */}
          {message && (
            <div className={`p-4 rounded-xl flex items-center mb-6 ${
              message.type === 'success' 
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-200' 
                : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200'
            }`}>
              {message.type === 'success' ? (
                <CheckCircle className="w-5 h-5 mr-2" />
              ) : (
                <AlertCircle className="w-5 h-5 mr-2" />
              )}
              {message.text}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* AI Configuration */}
          <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
            <button
              onClick={() => toggleSection('ai')}
              className="flex items-center justify-between w-full mb-6"
            >
              <div className="flex items-center">
                <div className={`p-2 bg-gradient-to-r ${colors.primary} rounded-lg mr-3`}>
                  <Bot className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-semibold ${colors.text}`}>AI Assistant Configuration</h2>
              </div>
              <ChevronDown className={`w-5 h-5 ${colors.textSecondary} transition-transform duration-200 ${expandedSections.has('ai') ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedSections.has('ai') && (
              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-medium ${colors.text} mb-2`}>
                    AI Provider
                  </label>
                  <select
                    value={settings.ai_config.provider}
                    onChange={(e) => setSettings({
                      ...settings,
                      ai_config: { ...settings.ai_config, provider: e.target.value }
                    })}
                    className={`w-full p-3 bg-white dark:bg-slate-700 border ${colors.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                  >
                    <option value="openai">OpenAI (GPT-4o Mini)</option>
                    <option value="anthropic">Anthropic (Claude 3.5 Sonnet)</option>
                    <option value="google">Google (Gemini Pro)</option>
                    <option value="mistral">Mistral AI</option>
                  </select>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.text} mb-2`}>
                    API Key
                    {settings.ai_config.api_key && (
                      <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Stored
                      </span>
                    )}
                  </label>
                  <div className="relative">
                    <input
                      type="password"
                      value={settings.ai_config.api_key}
                      onChange={(e) => setSettings({
                        ...settings,
                        ai_config: { ...settings.ai_config, api_key: e.target.value }
                      })}
                      className={`w-full p-3 pr-10 bg-white dark:bg-slate-700 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${
                        settings.ai_config.api_key 
                          ? 'border-green-300 dark:border-green-600 bg-green-50 dark:bg-green-900/20' 
                          : colors.border
                      }`}
                      placeholder={settings.ai_config.api_key ? "API key is stored" : "Enter your API key"}
                    />
                    <Key className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  </div>
                  <p className={`text-xs ${colors.textSecondary} mt-1`}>
                    {settings.ai_config.api_key 
                      ? "Your API key is stored securely and ready to use." 
                      : "Enter your API key above, then click 'Save API Key' to store it securely."
                    }
                  </p>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${colors.text} mb-2`}>
                    Model
                  </label>
                  <select
                    value={settings.ai_config.model}
                    onChange={(e) => setSettings({
                      ...settings,
                      ai_config: { ...settings.ai_config, model: e.target.value }
                    })}
                    className={`w-full p-3 bg-white dark:bg-slate-700 border ${colors.border} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200`}
                  >
                    <option value="gpt-4o-mini">GPT-4o Mini (Fast & Affordable)</option>
                    <option value="gpt-4o">GPT-4o (Most Capable)</option>
                    <option value="claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                    <option value="gemini-pro">Gemini Pro</option>
                    <option value="mistral-large">Mistral Large</option>
                  </select>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={saveSettings}
                    disabled={saving || !settings.ai_config.api_key}
                    className={`flex-1 px-4 py-2 bg-gradient-to-r ${colors.gradient} hover:${colors.gradientHover} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-all duration-200 flex items-center justify-center font-medium`}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'Saving...' : 'Save API Key'}
                  </button>
                  <button
                    onClick={testAIConnection}
                    disabled={saving || !settings.ai_config.api_key}
                    className="flex-1 px-4 py-2 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 flex items-center justify-center font-medium"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Test Connection
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Appearance & Theme */}
          <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
            <button
              onClick={() => toggleSection('appearance')}
              className="flex items-center justify-between w-full mb-6"
            >
              <div className="flex items-center">
                <div className={`p-2 bg-gradient-to-r ${colors.primary} rounded-lg mr-3`}>
                  <Palette className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-semibold ${colors.text}`}>Appearance & Themes</h2>
              </div>
              <ChevronDown className={`w-5 h-5 ${colors.textSecondary} transition-transform duration-200 ${expandedSections.has('appearance') ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedSections.has('appearance') && (
              <div className="space-y-6">
                {/* Theme Selection */}
                <div>
                  <h3 className={`font-medium ${colors.text} mb-3`}>Choose Theme</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {themes.map((theme) => (
                      <button
                        key={theme.id}
                        onClick={() => setTheme(theme.id)}
                        className={`p-3 rounded-lg border transition-all duration-200 text-left ${
                          currentTheme === theme.id
                            ? `bg-gradient-to-r ${theme.gradient}/10 border-${theme.gradient.split('-')[1]}-300 dark:border-${theme.gradient.split('-')[1]}-600`
                            : `bg-white dark:bg-slate-700 ${colors.border} hover:bg-slate-50 dark:hover:bg-slate-600`
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg bg-gradient-to-r ${theme.gradient}`}>
                            {theme.icon}
                          </div>
                          <div>
                            <div className={`font-medium ${colors.text}`}>{theme.name}</div>
                            <div className={`text-xs ${colors.textSecondary}`}>{theme.description}</div>
                          </div>
                          {currentTheme === theme.id && (
                            <Check className="w-4 h-4 text-green-500 ml-auto" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Particle Effects */}
                <div>
                  <h3 className={`font-medium ${colors.text} mb-3`}>Particle Effects</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                      <div>
                        <div className={`font-medium ${colors.text}`}>Enable Particles</div>
                        <div className={`text-sm ${colors.textSecondary}`}>Show floating particles and effects</div>
                      </div>
                      <button 
                        onClick={toggleParticles}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${isParticlesActive ? `bg-gradient-to-r ${colors.primary}` : 'bg-slate-200 dark:bg-slate-600'}`}
                      >
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${isParticlesActive ? 'translate-x-6' : 'translate-x-1'}`} />
                      </button>
                    </div>
                    
                    <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                      <div className={`font-medium ${colors.text} mb-2`}>Current Theme: {themes.find(t => t.id === currentTheme)?.name}</div>
                      <div className={`text-sm ${colors.textSecondary}`}>
                        Particles: {isParticlesActive ? 'Enabled' : 'Disabled'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Color Mode */}
                <div>
                  <h3 className={`font-medium ${colors.text} mb-3`}>Display Mode</h3>
                  <div className="p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-medium ${colors.text}`}>Color Mode</div>
                        <div className={`text-sm ${colors.textSecondary}`}>Follow system, or force light/dark</div>
                      </div>
                      <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-0.5">
                        <button onClick={()=>setColorMode('system')} className={`px-3 py-1.5 text-xs rounded-md ${colorMode==='system' ? 'bg-white dark:bg-slate-700 shadow' : ''}`}>System</button>
                        <button onClick={()=>setColorMode('light')} className={`px-3 py-1.5 text-xs rounded-md ${colorMode==='light' ? 'bg-white dark:bg-slate-700 shadow' : ''}`}>Light</button>
                        <button onClick={()=>setColorMode('dark')} className={`px-3 py-1.5 text-xs rounded-md ${colorMode==='dark' ? 'bg-white dark:bg-slate-700 shadow' : ''}`}>Dark</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Data Management */}
          <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
            <button
              onClick={() => toggleSection('data')}
              className="flex items-center justify-between w-full mb-6"
            >
              <div className="flex items-center">
                <div className={`p-2 bg-gradient-to-r ${colors.primary} rounded-lg mr-3`}>
                  <Database className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-semibold ${colors.text}`}>Data Management</h2>
              </div>
              <ChevronDown className={`w-5 h-5 ${colors.textSecondary} transition-transform duration-200 ${expandedSections.has('data') ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedSections.has('data') && (
              <div className="space-y-4">
                <button
                  onClick={exportData}
                  className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 flex items-center justify-center font-medium"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export All Data
                </button>
                
                <button
                  onClick={clearData}
                  className="w-full px-4 py-3 bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-red-700 dark:text-red-300 rounded-lg transition-all duration-200 flex items-center justify-center font-medium"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Clear All Data
                </button>
              </div>
            )}
          </div>

          {/* Account & Security */}
          <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg`}>
            <button
              onClick={() => toggleSection('security')}
              className="flex items-center justify-between w-full mb-6"
            >
              <div className="flex items-center">
                <div className={`p-2 bg-gradient-to-r ${colors.primary} rounded-lg mr-3`}>
                  <Shield className="w-5 h-5 text-white" />
                </div>
                <h2 className={`text-xl font-semibold ${colors.text}`}>Account & Security</h2>
              </div>
              <ChevronDown className={`w-5 h-5 ${colors.textSecondary} transition-transform duration-200 ${expandedSections.has('security') ? 'rotate-180' : ''}`} />
            </button>
            
            {expandedSections.has('security') && (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600">
                  <div>
                    <div className={`font-medium ${colors.text}`}>Admin User</div>
                    <div className={`text-sm ${colors.textSecondary}`}>admin@tess.com</div>
                  </div>
                  <User className="w-5 h-5 text-slate-400" />
                </div>

                <button className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 flex items-center justify-center font-medium">
                  <Shield className="w-4 h-4 mr-2" />
                  Change Password
                </button>
                
                <button className="w-full px-4 py-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-lg transition-all duration-200 flex items-center justify-center font-medium">
                  <Bell className="w-4 h-4 mr-2" />
                  Notification Preferences
                </button>
              </div>
            )}
          </div>
        </div>

        {/* System Information */}
        <div className={`${colors.surface} backdrop-blur-xl rounded-xl p-6 border ${colors.border} shadow-lg mt-6`}>
          <h2 className={`text-xl font-semibold ${colors.text} mb-4`}>System Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className={colors.textSecondary}>Version:</span>
              <span className={`ml-2 font-medium ${colors.text}`}>1.0.0</span>
            </div>
            <div>
              <span className={colors.textSecondary}>Database:</span>
              <span className={`ml-2 font-medium ${colors.text}`}>SQLite</span>
            </div>
            <div>
              <span className={colors.textSecondary}>Last Updated:</span>
              <span className={`ml-2 font-medium ${colors.text}`}>{new Date().toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 